import {
	lookupPaceCustomer,
	listPaceCustomerPaymentMethods,
	listPaceShipVia,
	listPaceTerms,
	listPaceCsrs,
	listPaceSalespersons,
	listPaceJobTypes,
	listPaceManufacturingLocations,
	saveStorePaceAccount,
	saveStoreDefaults,
	addShippingMethodMapping,
	addPaymentTermMapping,
	addStorePaymentMethod,
} from './queries';

// ERP ERP customer numbers are zero-padded to 8 characters (e.g. "1656" ->
// "00001656") -- confirmed against a live customer lookup.
export const padCustomerNumber = (value) => value.trim().padStart(8, '0');

const defaultPaymentMethods = { CREDIT_CARD: true, PURCHASE_ORDER: false };

// Fetches the ERP customer plus every piece of reference data needed to
// apply sensible defaults, and validates the customer number resolves in
// ERP before anything is written to the store.
export const lookupPaceCustomerBundle = async (customerNumber) => {
	const named = [
		['Customer', lookupPaceCustomer(customerNumber)],
		['Ship Via', listPaceShipVia()],
		['Terms', listPaceTerms()],
		['CSRs', listPaceCsrs()],
		['Salespersons', listPaceSalespersons()],
		['Job Types', listPaceJobTypes()],
		['Manufacturing Locations', listPaceManufacturingLocations()],
	];
	const [customerResult, shipViaResult, termsResult, csrsResult, salespersonsResult, jobTypesResult, mfgResult] =
		await Promise.all(named.map(([, promise]) => promise));

	if (!customerResult?.success) {
		return { success: false, error: customerResult?.error?.message || 'Customer not found in ERP.' };
	}

	const results = [customerResult, shipViaResult, termsResult, csrsResult, salespersonsResult, jobTypesResult, mfgResult];
	const failed = named.filter((_, i) => !results[i]?.success).map(([label]) => label);
	if (failed.length) {
		return { success: false, error: `Couldn't load ERP reference data: ${failed.join(', ')}. Try again.` };
	}

	const customer = customerResult.data;
	const shipVia = shipViaResult.data;
	const terms = termsResult.data;
	const jobTypes = jobTypesResult.data;

	// Same defaulting rules the confirm-step used to apply manually.
	const defaultJobType = jobTypes.find((o) => (o.description || '').toLowerCase() === 'dsf order');
	const upsGround = shipVia.find((o) => (o.description || '').toLowerCase() === 'ups ground');

	const paymentMethodsResult = await listPaceCustomerPaymentMethods(customerNumber);
	const pacePaymentMethods = paymentMethodsResult?.success ? paymentMethodsResult.data : [];
	const paymentMethods =
		pacePaymentMethods.length > 0
			? {
					CREDIT_CARD: pacePaymentMethods.some((m) => m.creditCardProcessing),
					PURCHASE_ORDER: pacePaymentMethods.some((m) => m.poRequired),
				}
			: defaultPaymentMethods;

	return { success: true, data: { customer, terms, defaultJobType, upsGround, paymentMethods } };
};

// From here on, the store already exists. saveStorePaceAccount is the
// connection itself (it's what Setup/the ERP tab check to decide "connected"
// or not) so it isn't optional -- unlike queries.js's other calls, which
// resolve instead of rejecting on a failed request, a false success here
// must stop the rest of this function instead of silently continuing on to
// create defaults/mappings for a connection that was never actually saved.
// Everything after that call is genuinely best-effort: anything that doesn't
// stick just leaves that item unchecked on the Finish Setup page for the
// admin to fix via its "Manage" links, rather than blocking on a convenience
// default failing to apply.
export const applyPaceCustomerDefaults = async (storeId, customerNumber, { customer, terms, defaultJobType, upsGround, paymentMethods }) => {
	const paceAccountResult = await saveStorePaceAccount(storeId, { paceCustomerNumber: customerNumber });
	if (!paceAccountResult?.success) {
		return { success: false, error: paceAccountResult?.error?.message || 'Failed to save the ERP connection.' };
	}

	await saveStoreDefaults(storeId, {
		erpCsrId: customer.csr != null ? String(customer.csr) : undefined,
		erpSalespersonId: customer.salesPerson != null ? String(customer.salesPerson) : undefined,
		erpJobTypeId: defaultJobType ? String(defaultJobType.id) : undefined,
		erpManufacturingLocationId: customer.manufacturingLocation != null ? String(customer.manufacturingLocation) : undefined,
	});

	if (upsGround) {
		await addShippingMethodMapping(storeId, {
			shippingTitle: upsGround.description,
			erpShipViaId: upsGround.id,
		});
	}

	// Not admin-facing -- every ERP term the customer could send on an
	// order is saved as a mapping so nothing fails to resolve at
	// submission time (see customerMapping.service.ts).
	for (const term of terms) {
		await addPaymentTermMapping(storeId, {
			paymentTermsName: term.description || `Terms ${term.id}`,
			erpTermsId: term.id,
		});
	}

	for (const method of Object.keys(paymentMethods).filter((m) => paymentMethods[m])) {
		await addStorePaymentMethod(storeId, method);
	}

	return { success: true };
};
